// Function to convert HEX to RGB
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  hex = hex.replace("#", "");
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
}

// Function to convert RGBA to RGB
export function rgbaToRgb(rgba: string): { r: number; g: number; b: number } {
  const rgbaMatch = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*\d*\.?\d+)?\)/);
  if (!rgbaMatch) throw new Error("Invalid RGBA color format");
  return {
    r: parseInt(rgbaMatch[1]),
    g: parseInt(rgbaMatch[2]),
    b: parseInt(rgbaMatch[3]),
  };
}

// Function to convert RGB to HEX
export function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()
  );
}

// Function to calculate relative luminance
export function getLuminance(r: number, g: number, b: number): number {
  let rgb = [r / 255, g / 255, b / 255];
  for (let i = 0; i < 3; i++) {
    if (rgb[i] <= 0.03928) {
      rgb[i] = rgb[i] / 12.92;
    } else {
      rgb[i] = Math.pow((rgb[i] + 0.055) / 1.055, 2.4);
    }
  }

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

// Function to calculate contrast ratio between two colors
export function getContrastRatio(color1: string, color2: string): number {
  let rgb1 = parseColor(color1);
  let rgb2 = parseColor(color2);
  let luminance1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  let luminance2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  let light = Math.max(luminance1, luminance2);
  let dark = Math.min(luminance1, luminance2);

  return (light + 0.05) / (dark + 0.05);
}

// Function to lighten or darken a color by a certain percentage
export function adjustColorBrightness(hex: string, amount: number): string {
  let rgb = parseColor(hex);
  rgb.r = Math.min(Math.max(0, rgb.r + amount), 255);
  rgb.g = Math.min(Math.max(0, rgb.g + amount), 255);
  rgb.b = Math.min(Math.max(0, rgb.b + amount), 255);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

// Function to parse HEX or RGBA color
export function parseColor(color: string): { r: number; g: number; b: number } {
  if (color.startsWith("#")) {
    return hexToRgb(color);
  } else if (color.startsWith("rgb")) {
    return rgbaToRgb(color);
  } else {
    return { r: 0, g: 0, b: 0 };
    console.error("Unsupported color format");
  }
}

// Define the structure of the returned object
interface ColorDetails {
  bgColor: string; // HEX code for normal background color
  bgHoverColor: string; // HEX code for hover background color
  labelColor: string; // HEX code for label (text) color
}

// Function to generate compliant background, hover, and label colors
export default function getColors(backgroundColor: string): ColorDetails {
  const bgRgb = parseColor(backgroundColor);
  const luminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);

  // Calculate contrast ratios with black and white text
  const contrastWithBlack = getContrastRatio(backgroundColor, "#000000");
  const contrastWithWhite = getContrastRatio(backgroundColor, "#FFFFFF");

  // Determine label color based on contrast ratio
  let labelColor: string;
  if (contrastWithBlack >= contrastWithWhite) {
    labelColor = "#000000"; // Use black for high contrast
  } else {
    labelColor = "#FFFFFF"; // Use white for high contrast
  }

  // Adjust the hover background color based on the luminance of the original background color
  let bgHoverColor: string;
  if (luminance > 0.85) {
    // Darken the color by approximately 10%
    bgHoverColor = adjustColorBrightness(backgroundColor, -25); // Darker by 10%
  } else {
    // Lighten the color by approximately 10%
    bgHoverColor = adjustColorBrightness(backgroundColor, 25); // Lighter by 10%
  }

  // Ensure hover background color is still WCAG compliant with text
  while (getContrastRatio(bgHoverColor, labelColor) < 3) {
    if (luminance > 0.85) {
      bgHoverColor = adjustColorBrightness(bgHoverColor, -5); // Darken more if not compliant
    } else {
      bgHoverColor = adjustColorBrightness(bgHoverColor, 5); // Lighten more if not compliant
    }
  }

  // Return the object with the colors
  return {
    bgColor: backgroundColor, // Original background color
    bgHoverColor, // Hover background color
    labelColor, // The label color (black or white)
  };
}